import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { FileCheck, Plus, Pencil, Trash2, Search, ArrowDownCircle, ArrowUpCircle, CheckCircle, Clock, Upload, FileText, X, Check, ChevronsUpDown, Download, Eye, Briefcase, ArrowUpDown, ArrowUp, ArrowDown, LayoutGrid, List } from 'lucide-react';
import { cn } from '../../lib/utils';
import DocumentPreviewModal from '../../components/DocumentPreviewModal';
import JobDetailPopup from '../../components/JobDetailPopup';

const AccountingChecks = () => {
  const navigate = useNavigate();
  const [activeMainTab, setActiveMainTab] = useState('alinan');
  const [activeSubTab, setActiveSubTab] = useState('odenecek');
  const [cekler, setCekler] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCek, setEditingCek] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDocPreview, setShowDocPreview] = useState(false);
  const [previewBelgeler, setPreviewBelgeler] = useState([]);
  
  // Görünüm modu - 'card' veya 'table'
  const [viewMode, setViewMode] = useState('card');

  // İş referansı state
  const [pendingJobAction, setPendingJobAction] = useState(null);
  
  // Job detail popup state
  const [showJobPopup, setShowJobPopup] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'vade_tarihi', direction: 'desc' });

  // Dropdown data
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [hesapKartlari, setHesapKartlari] = useState([]);
  
  // Combobox states
  const [cariOpen, setCariOpen] = useState(false);
  const [hesapKartiOpen, setHesapKartiOpen] = useState(false);

  const [form, setForm] = useState({
    tip: 'alinan',
    cek_no: '',
    banka: '',
    tutar: '',
    duzenleme_tarihi: '',
    vade_tarihi: '',
    kesideci: '',
    alici: '',
    aciklama: '',
    cari_hesap_id: '',
    cari_hesap_adi: '',
    hesap_karti_id: '',
    hesap_karti_adi: '',
    belge_url: '',
    belge_adi: ''
  });

  const fetchCekler = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/accounting/cekler?tip=${activeMainTab}&durum=${activeSubTab}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCekler(data);
      }
    } catch (error) {
      console.error('Çekler alınamadı:', error);
    }
  }, [activeMainTab, activeSubTab]);

  const fetchDropdownData = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [cariRes, hesapKartiRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cari-hesaplar`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/hesap-kartlari`, { headers })
      ]);

      if (cariRes.ok) setCariHesaplar(await cariRes.json());
      if (hesapKartiRes.ok) setHesapKartlari(await hesapKartiRes.json());
    } catch (error) {
      console.error('Dropdown verileri alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchCekler();
    fetchDropdownData();
  }, [fetchCekler, fetchDropdownData]);

  // İş referansı ile gelen işlemi kontrol et
  useEffect(() => {
    const pendingAction = localStorage.getItem('pending_job_action');
    if (pendingAction) {
      try {
        const action = JSON.parse(pendingAction);
        if (action.action_type === 'cek') {
          setPendingJobAction(action);
          // Formu aç
          setShowModal(true);
          // localStorage'dan sil
          localStorage.removeItem('pending_job_action');
          toast.info(`"${action.job_konu}" işi için çek kaydı ekleniyor`);
        }
      } catch (e) {
        console.error('Job action parse error:', e);
      }
    }
  }, []);

  const openNewModal = () => {
    const today = new Date().toISOString().split('T')[0];
    setEditingCek(null);
    setForm({
      tip: activeMainTab,
      cek_no: '',
      banka: '',
      tutar: '',
      duzenleme_tarihi: today,
      vade_tarihi: '',
      kesideci: '',
      alici: '',
      aciklama: '',
      cari_hesap_id: '',
      cari_hesap_adi: '',
      hesap_karti_id: '',
      hesap_karti_adi: '',
      belgeler: []  // Çoklu belge desteği
    });
    setShowModal(true);
  };

  const openEditModal = (cek) => {
    setEditingCek(cek);
    setForm({
      tip: cek.tip || activeMainTab,
      cek_no: cek.cek_no || '',
      banka: cek.banka || '',
      tutar: cek.tutar || '',
      duzenleme_tarihi: cek.duzenleme_tarihi || '',
      vade_tarihi: cek.vade_tarihi || '',
      kesideci: cek.kesideci || '',
      alici: cek.alici || '',
      aciklama: cek.aciklama || '',
      cari_hesap_id: cek.cari_hesap_id || '',
      cari_hesap_adi: cek.cari_hesap_adi || '',
      hesap_karti_id: cek.hesap_karti_id || '',
      hesap_karti_adi: cek.hesap_karti_adi || '',
      belgeler: cek.belgeler || (cek.belge_url ? [{ url: cek.belge_url, name: cek.belge_adi || 'Belge' }] : [])  // Çoklu belge desteği
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.cek_no || !form.tutar) {
      toast.error('Çek no ve tutar gereklidir');
      return;
    }

    try {
      const token = localStorage.getItem('fortis_token');
      const url = editingCek
        ? `${process.env.REACT_APP_BACKEND_URL}/api/accounting/cekler/${editingCek.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/accounting/cekler`;

      // Always set durum to odenecek for new checks
      const submitData = {
        ...form,
        durum: editingCek ? editingCek.durum : 'odenecek'
      };
      
      // İş referansı ekle
      if (pendingJobAction && !editingCek) {
        submitData.job_id = pendingJobAction.job_id;
        submitData.job_konu = pendingJobAction.job_konu;
      }

      // ONAYLANAN İŞ AKIŞI: Gerçek kaydı YAZMA! Sadece Taslak (pending_transaction) olarak ekle.
      // Gerçek Çek kaydı, Yönetim Kurulu onayladıktan sonra oluşturulacak.
      if (pendingJobAction && !editingCek) {
        try {
          const pendingPayload = {
            type: 'cek',
            tarih: submitData.tarih,
            tutar: parseFloat(submitData.tutar) || 0,
            vade_tarihi: submitData.vade_tarihi || submitData.tarih,
            cek_no: submitData.cek_no || '',
            banka: submitData.banka || '',
            cek_turu: submitData.cek_turu || 'alinan',
            kesideci: submitData.kesideci || '',
            kesideci_cari_id: submitData.kesideci_cari_id || null,
            alici: submitData.alici || '',
            alici_cari_id: submitData.alici_cari_id || null,
            aciklama: submitData.aciklama || '',
            form_data: { ...submitData }
          };
          const pendingRes = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/accounting/isler/${pendingJobAction.job_id}/pending-transactions`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify(pendingPayload)
            }
          );
          if (!pendingRes.ok) {
            const err = await pendingRes.json().catch(() => ({}));
            toast.error(err.detail || 'Taslak işlem eklenemedi');
            return;
          }
          await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/accounting/isler/${pendingJobAction.job_id}/complete-approved`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ note: 'Çek kaydı taslak olarak eklendi' })
            }
          );
          toast.success(`"${pendingJobAction.job_konu}" işi için çek taslağı oluşturuldu ve Yönetim Kurulu onayına gönderildi`);
          setPendingJobAction(null);
          localStorage.removeItem('pending_job_action');
          setShowModal(false);
          navigate('/accounting/jobs');
          return;
        } catch (e) {
          console.error('Taslak çek ekleme hatası:', e);
          toast.error('Taslak çek eklenemedi');
          return;
        }
      }

      const response = await fetch(url, {
        method: editingCek ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success(editingCek ? 'Çek güncellendi' : 'Çek eklendi');
        setShowModal(false);
        fetchCekler();
      } else {
        toast.error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu çeki silmek istediğinizden emin misiniz?')) return;

    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cekler/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Çek silindi');
        fetchCekler();
      }
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const newBelgeler = [];
    for (const file of files) {
      const reader = new FileReader();
      const fileData = await new Promise((resolve) => {
        reader.onload = (event) => {
          resolve({
            url: event.target.result,
            name: file.name
          });
        };
        reader.readAsDataURL(file);
      });
      newBelgeler.push(fileData);
    }
    
    setForm(prev => ({
      ...prev,
      belgeler: [...prev.belgeler, ...newBelgeler]
    }));
    toast.success(`${files.length} belge eklendi`);
    e.target.value = '';
  };

  const removeBelge = (index) => {
    setForm(prev => ({
      ...prev,
      belgeler: prev.belgeler.filter((_, i) => i !== index)
    }));
  };

  // Excel export
  const exportToExcel = () => {
    const headers = ['Çek No', 'Cari Hesap', 'Banka', 'Tutar', 'Düzenleme Tarihi', 'Vade Tarihi', 'Keşideci', 'Alıcı', 'Durum', 'Açıklama'];
    const rows = filteredCekler.map(c => [
      c.cek_no || '',
      c.cari_hesap_adi || '',
      c.banka || '',
      c.tutar || 0,
      c.duzenleme_tarihi || '',
      c.vade_tarihi || '',
      c.kesideci || '',
      c.alici || '',
      c.durum === 'odenmis' ? 'Ödenmiş' : 'Ödenecek',
      c.aciklama || ''
    ]);
    
    const BOM = '\uFEFF';
    const csvContent = BOM + [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `cekler_${activeMainTab}_${activeSubTab}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Excel dosyası indirildi');
  };

  const filteredCekler = cekler.filter(c =>
    c.cek_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.banka?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.kesideci?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.alici?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cari_hesap_adi?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sorting function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3 w-3 ml-1 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1 text-blue-600" />
      : <ArrowDown className="h-3 w-3 ml-1 text-blue-600" />;
  };

  // Sortable header component
  const SortableHeader = ({ children, columnKey, className = "" }) => (
    <TableHead 
      className={cn("cursor-pointer hover:bg-gray-100 select-none", className)}
      onClick={() => handleSort(columnKey)}
    >
      <div className="flex items-center">
        {children}
        <SortIcon columnKey={columnKey} />
      </div>
    </TableHead>
  );

  // Sort data
  const sortedCekler = useMemo(() => {
    return [...filteredCekler].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle numeric values
      if (['tutar'].includes(sortConfig.key)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      // Handle dates
      if (['vade_tarihi', 'duzenleme_tarihi'].includes(sortConfig.key)) {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      
      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCekler, sortConfig]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  // Statistics
  const allCekler = cekler;
  const alinanOdenecek = allCekler.filter(c => c.tip === 'alinan' && c.durum === 'odenecek');
  const alinanOdenmis = allCekler.filter(c => c.tip === 'alinan' && c.durum === 'odenmis');
  const verilenOdenecek = allCekler.filter(c => c.tip === 'verilen' && c.durum === 'odenecek');
  const verilenOdenmis = allCekler.filter(c => c.tip === 'verilen' && c.durum === 'odenmis');

  // Tutarı yazıya çevir
  const tutarYaziya = (tutar) => {
    if (!tutar || tutar === 0) return '';
    const birler = ['', 'BİR', 'İKİ', 'ÜÇ', 'DÖRT', 'BEŞ', 'ALTI', 'YEDİ', 'SEKİZ', 'DOKUZ'];
    const onlar = ['', 'ON', 'YİRMİ', 'OTUZ', 'KIRK', 'ELLİ', 'ALTMIŞ', 'YETMİŞ', 'SEKSEN', 'DOKSAN'];
    
    const sayi = Math.floor(parseFloat(tutar));
    const kurus = Math.round((parseFloat(tutar) - sayi) * 100);
    
    let sonuc = '';
    
    if (sayi >= 1000000) {
      const milyon = Math.floor(sayi / 1000000);
      if (milyon === 1) sonuc += 'BİRMİLYON';
      else sonuc += birler[milyon] + 'MİLYON';
    }
    
    const kalanMilyon = sayi % 1000000;
    if (kalanMilyon >= 1000) {
      const bin = Math.floor(kalanMilyon / 1000);
      if (bin === 1) sonuc += 'BİN';
      else {
        const binYuz = Math.floor(bin / 100);
        const binOn = Math.floor((bin % 100) / 10);
        const binBir = bin % 10;
        if (binYuz > 0) sonuc += (binYuz === 1 ? '' : birler[binYuz]) + 'YÜZ';
        if (binOn > 0) sonuc += onlar[binOn];
        if (binBir > 0) sonuc += birler[binBir];
        sonuc += 'BİN';
      }
    }
    
    const kalan = sayi % 1000;
    const yuz = Math.floor(kalan / 100);
    const on = Math.floor((kalan % 100) / 10);
    const bir = kalan % 10;
    
    if (yuz > 0) sonuc += (yuz === 1 ? '' : birler[yuz]) + 'YÜZ';
    if (on > 0) sonuc += onlar[on];
    if (bir > 0) sonuc += birler[bir];
    
    sonuc += 'TL';
    
    if (kurus > 0) {
      const kurusOn = Math.floor(kurus / 10);
      const kurusBir = kurus % 10;
      if (kurusOn > 0) sonuc += onlar[kurusOn];
      if (kurusBir > 0) sonuc += birler[kurusBir];
      sonuc += 'KR';
    }
    
    return sonuc || 'SIFIRTL';
  };

  // Çek Kartı Komponenti - Gerçek Banka Çeki Görünümü
  const CheckCard = ({ cek }) => {
    const isAlinan = cek.tip === 'alinan';
    const isOdenmis = cek.durum === 'odenmis';
    
    // Renk şeması
    const baseColor = isAlinan 
      ? (isOdenmis ? 'from-emerald-50 to-emerald-100 border-emerald-300' : 'from-emerald-100 to-emerald-200 border-emerald-400')
      : (isOdenmis ? 'from-orange-50 to-orange-100 border-orange-300' : 'from-orange-100 to-orange-200 border-orange-400');
    
    const headerColor = isAlinan
      ? (isOdenmis ? 'bg-emerald-600/80' : 'bg-emerald-700')
      : (isOdenmis ? 'bg-orange-600/80' : 'bg-orange-700');
    
    const accentColor = isAlinan ? 'text-emerald-700' : 'text-orange-700';
    const lightAccent = isAlinan ? 'text-emerald-600' : 'text-orange-600';

    return (
      <div 
        className={`relative bg-gradient-to-br ${baseColor} rounded-xl border-2 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group`}
        style={{ height: '280px' }}
        data-testid={`check-card-${cek.id}`}
      >
        {/* Ödenmiş damgası */}
        {isOdenmis && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[-15deg] z-10 pointer-events-none">
            <div className="border-4 border-green-600 text-green-600 px-6 py-2 rounded-lg font-bold text-2xl opacity-40 bg-white/50">
              ÖDENMİŞ
            </div>
          </div>
        )}

        {/* Üst Bant - Banka Adı */}
        <div className={`${headerColor} px-4 py-2 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <FileCheck className="h-4 w-4 text-white" />
            </div>
            <span className="text-white font-semibold text-sm tracking-wide">
              {cek.banka || 'BANKA'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={isOdenmis ? 'bg-white/30 text-white border-0' : 'bg-white text-gray-700 border-0'}>
              {isOdenmis ? <CheckCircle className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
              {isOdenmis ? 'Ödenmiş' : 'Ödenecek'}
            </Badge>
            {/* Detay Butonu */}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 bg-white/20 hover:bg-white/40 text-white rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                setDetailRecord(cek);
                setShowDetailModal(true);
              }}
              title="Detay Görüntüle"
            >
              <Eye className="h-3.5 w-3.5" />
            </Button>
            {/* Düzenle Butonu */}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 bg-white/20 hover:bg-blue-400/50 text-white rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                setEditingCek(cek);
                setShowModal(true);
              }}
              title="Düzenle"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            {/* Sil Butonu */}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 bg-white/20 hover:bg-red-400/50 text-white rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Bu çek kaydını silmek istediğinize emin misiniz?')) {
                  handleDelete(cek.id);
                }
              }}
              title="Sil"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Çek İçeriği */}
        <div className="p-4 space-y-3">
          {/* Üst Satır - Tarih ve Çek No */}
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Düzenleme:</span>
                <span className={`font-mono text-sm font-medium ${accentColor}`}>
                  {formatDate(cek.duzenleme_tarihi)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Vade:</span>
                <span className={`font-mono text-sm font-bold ${accentColor}`}>
                  {formatDate(cek.vade_tarihi)}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500">Çek No</div>
              <div className={`font-mono text-lg font-bold ${accentColor} tracking-wider`}>
                {cek.cek_no}
              </div>
              {cek.seri_no && (
                <div className="text-xs text-gray-400 font-mono">{cek.seri_no}</div>
              )}
            </div>
          </div>

          {/* Tutar Kutusu */}
          <div className="flex items-center justify-end">
            <div className={`bg-white/80 border-2 ${isAlinan ? 'border-emerald-400' : 'border-orange-400'} rounded-lg px-4 py-2 shadow-inner`}>
              <div className="text-xs text-gray-500 text-right">TUTAR</div>
              <div className={`font-mono text-xl font-bold ${accentColor} tracking-wide`}>
                ₺{parseFloat(cek.tutar || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* Yazı ile Tutar */}
          <div className={`bg-white/60 rounded px-3 py-1.5 border ${isAlinan ? 'border-emerald-200' : 'border-orange-200'}`}>
            <div className="text-[10px] text-gray-400 uppercase tracking-wide">Yalnız</div>
            <div className={`text-xs font-semibold ${lightAccent} tracking-wide truncate`} title={tutarYaziya(cek.tutar)}>
              #{tutarYaziya(cek.tutar)}#
            </div>
          </div>

          {/* Alt Bilgiler - Keşideci / Alıcı */}
          <div className="flex items-end justify-between pt-1">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-gray-400 uppercase">
                {isAlinan ? 'Keşideci' : 'Alıcı'}
              </div>
              <div className={`text-sm font-medium ${accentColor} truncate`}>
                {isAlinan ? (cek.kesideci || cek.cari_hesap_adi || '-') : (cek.alici || '-')}
              </div>
            </div>
            <div className="flex items-center gap-2 ml-2">
              {/* Belge ikonu */}
              {(cek.belgeler?.length > 0 || cek.belge_url) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (cek.belgeler?.length > 0) {
                      setPreviewBelgeler(cek.belgeler);
                      setShowDocPreview(true);
                    } else if (cek.belge_url) {
                      window.open(cek.belge_url, '_blank');
                    }
                  }}
                  className="flex items-center gap-1 px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded text-blue-700 transition-colors"
                  title="Belgeleri Görüntüle"
                >
                  <FileText className="h-3 w-3" />
                  <span className="text-xs">{cek.belgeler?.length || 1}</span>
                </button>
              )}
              {/* İş Kaydı ikonu */}
              {cek.job_id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedJobId(cek.job_id);
                    setShowJobPopup(true);
                  }}
                  className="flex items-center gap-1 px-2 py-1 bg-indigo-100 hover:bg-indigo-200 rounded text-indigo-700 transition-colors"
                  title="İş Kaydını Görüntüle"
                >
                  <Briefcase className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Köşe Dekorasyon */}
        <div className={`absolute bottom-0 right-0 w-16 h-16 ${isAlinan ? 'bg-emerald-500/10' : 'bg-orange-500/10'} rounded-tl-full`} />
      </div>
    );
  };

  // Çek Kartları Grid Görünümü
  const renderCheckCards = () => (
    <div className="space-y-4">
      {sortedCekler.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-xl border-2 border-dashed">
          <FileCheck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">
            {activeSubTab === 'odenecek' ? 'Ödenecek çek bulunamadı' : 'Ödenmiş çek bulunamadı'}
          </p>
          <p className="text-sm text-gray-400 mt-1">Yeni çek eklemek için yukarıdaki butonu kullanın</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sortedCekler.map((cek) => (
            <CheckCard key={cek.id} cek={cek} />
          ))}
        </div>
      )}
    </div>
  );

  const renderCheckTable = () => (
    <div className="rounded-lg border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <SortableHeader columnKey="seri_no">Seri No</SortableHeader>
            <SortableHeader columnKey="cek_no">Çek No</SortableHeader>
            <SortableHeader columnKey="cari_hesap_adi">Cari Hesap</SortableHeader>
            <SortableHeader columnKey="banka">Banka</SortableHeader>
            <SortableHeader columnKey="tutar">Tutar</SortableHeader>
            <SortableHeader columnKey="vade_tarihi">Vade</SortableHeader>
            <SortableHeader columnKey={activeMainTab === 'alinan' ? 'kesideci' : 'alici'}>
              {activeMainTab === 'alinan' ? 'Keşideci' : 'Alıcı'}
            </SortableHeader>
            <TableHead>Durum</TableHead>
            <TableHead>Belgeler</TableHead>
            <SortableHeader columnKey="aciklama">Açıklama</SortableHeader>
            <TableHead className="text-center">İş Kaydı</TableHead>
            <TableHead className="text-right">Detay</TableHead>
            <TableHead className="text-center">İşlemler</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedCekler.length === 0 ? (
            <TableRow>
              <TableCell colSpan={13} className="text-center py-8 text-gray-500">
                {activeSubTab === 'odenecek' ? 'Ödenecek çek bulunamadı' : 'Ödenmiş çek bulunamadı'}
              </TableCell>
            </TableRow>
          ) : (
            sortedCekler.map((cek) => (
              <TableRow key={cek.id} data-testid={`cek-row-${cek.id}`}>
                <TableCell className="font-mono text-sm text-gray-600">{cek.seri_no || '-'}</TableCell>
                <TableCell className="font-mono font-medium">{cek.cek_no}</TableCell>
                <TableCell>{cek.cari_hesap_adi || '-'}</TableCell>
                <TableCell>{cek.banka || '-'}</TableCell>
                <TableCell className="font-semibold text-blue-600">
                  {formatCurrency(cek.tutar)}
                </TableCell>
                <TableCell>{formatDate(cek.vade_tarihi)}</TableCell>
                <TableCell>{activeMainTab === 'alinan' ? cek.kesideci : cek.alici || '-'}</TableCell>
                <TableCell>
                  {cek.durum === 'odenmis' ? (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Ödenmiş
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                      <Clock className="h-3 w-3 mr-1" />
                      Ödenecek
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {cek.belgeler && cek.belgeler.length > 0 ? (
                    <button
                      onClick={() => {
                        setPreviewBelgeler(cek.belgeler);
                        setShowDocPreview(true);
                      }}
                      className="flex items-center gap-1 cursor-pointer hover:underline"
                      data-testid={`belgeler-link-${cek.id}`}
                    >
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="text-xs text-blue-600">{cek.belgeler.length} belge</span>
                    </button>
                  ) : cek.belge_url ? (
                    <a href={cek.belge_url} target="_blank" rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      Görüntüle
                    </a>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  <span className="text-xs text-gray-600 max-w-[150px] truncate block" title={cek.aciklama}>
                    {cek.aciklama || '-'}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  {cek.job_id ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedJobId(cek.job_id);
                        setShowJobPopup(true);
                      }}
                      className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                      title="İş Kaydını Görüntüle"
                    >
                      <Briefcase className="h-4 w-4" />
                    </Button>
                  ) : (
                    <span className="text-gray-300">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDetailRecord(cek);
                      setShowDetailModal(true);
                    }}
                    title="Detay Görüntüle"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingCek(cek);
                        setShowModal(true);
                      }}
                      title="Düzenle"
                      className="h-8 w-8 p-0 hover:bg-blue-100 hover:text-blue-600"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (window.confirm('Bu çek kaydını silmek istediğinize emin misiniz?')) {
                          handleDelete(cek.id);
                        }
                      }}
                      title="Sil"
                      className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="accounting-checks">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Çekler</h1>
          <p className="text-gray-500">Alınan ve verilen çeklerin takibi</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={exportToExcel}
            className="gap-2"
            data-testid="export-excel-btn"
          >
            <Download className="h-4 w-4" />
            Excel İndir
          </Button>
          <Button
            onClick={openNewModal}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-cek-btn"
          >
            <Plus className="h-4 w-4" />
            Çek Ekle
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-500 rounded-lg">
                <ArrowDownCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-emerald-600 font-medium">Alınan (Ödenecek)</p>
                <p className="text-2xl font-bold text-emerald-900">{alinanOdenecek.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500 rounded-lg">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Alınan (Ödenmiş)</p>
                <p className="text-2xl font-bold text-green-900">{alinanOdenmis.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-500 rounded-lg">
                <ArrowUpCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-orange-600 font-medium">Verilen (Ödenecek)</p>
                <p className="text-2xl font-bold text-orange-900">{verilenOdenecek.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-500 rounded-lg">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-red-600 font-medium">Verilen (Ödenmiş)</p>
                <p className="text-2xl font-bold text-red-900">{verilenOdenmis.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs - Alınan / Verilen */}
      <Tabs value={activeMainTab} onValueChange={(v) => { setActiveMainTab(v); setActiveSubTab('odenecek'); }} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="alinan" className="gap-2">
            <ArrowDownCircle className="h-4 w-4" />
            Alınan Çekler
          </TabsTrigger>
          <TabsTrigger value="verilen" className="gap-2">
            <ArrowUpCircle className="h-4 w-4" />
            Verilen Çekler
          </TabsTrigger>
        </TabsList>

        <TabsContent value="alinan" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <ArrowDownCircle className="h-5 w-5 text-emerald-600" />
                Alınan Çekler
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Sub Tabs - Ödenmiş / Ödenecek */}
              <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="odenecek" className="gap-2">
                    <Clock className="h-4 w-4" />
                    Ödenecek Çekler
                  </TabsTrigger>
                  <TabsTrigger value="odenmis" className="gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Ödenmiş Çekler
                  </TabsTrigger>
                </TabsList>

                {/* Search ve Görünüm Toggle */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Çek no, cari hesap, banka veya keşideci ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="cek-search"
                    />
                  </div>
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                    <Button
                      variant={viewMode === 'card' ? 'default' : 'ghost'}
                      size="sm"
                      className={cn("h-8 w-8 p-0", viewMode === 'card' && "bg-white shadow-sm")}
                      onClick={() => setViewMode('card')}
                      title="Çek Kartı Görünümü"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'table' ? 'default' : 'ghost'}
                      size="sm"
                      className={cn("h-8 w-8 p-0", viewMode === 'table' && "bg-white shadow-sm")}
                      onClick={() => setViewMode('table')}
                      title="Tablo Görünümü"
                    >
                      <List className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <TabsContent value="odenecek">
                  {viewMode === 'card' ? renderCheckCards() : renderCheckTable()}
                </TabsContent>
                <TabsContent value="odenmis">
                  {viewMode === 'card' ? renderCheckCards() : renderCheckTable()}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="verilen" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <ArrowUpCircle className="h-5 w-5 text-orange-600" />
                Verilen Çekler
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Sub Tabs - Ödenmiş / Ödenecek */}
              <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="odenecek" className="gap-2">
                    <Clock className="h-4 w-4" />
                    Ödenecek Çekler
                  </TabsTrigger>
                  <TabsTrigger value="odenmis" className="gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Ödenmiş Çekler
                  </TabsTrigger>
                </TabsList>

                {/* Search ve Görünüm Toggle */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Çek no, cari hesap, banka veya alıcı ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                    <Button
                      variant={viewMode === 'card' ? 'default' : 'ghost'}
                      size="sm"
                      className={cn("h-8 w-8 p-0", viewMode === 'card' && "bg-white shadow-sm")}
                      onClick={() => setViewMode('card')}
                      title="Çek Kartı Görünümü"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'table' ? 'default' : 'ghost'}
                      size="sm"
                      className={cn("h-8 w-8 p-0", viewMode === 'table' && "bg-white shadow-sm")}
                      onClick={() => setViewMode('table')}
                      title="Tablo Görünümü"
                    >
                      <List className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <TabsContent value="odenecek">
                  {viewMode === 'card' ? renderCheckCards() : renderCheckTable()}
                </TabsContent>
                <TabsContent value="odenmis">
                  {viewMode === 'card' ? renderCheckCards() : renderCheckTable()}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Check Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-indigo-600" />
              {editingCek ? 'Çek Düzenle' : 'Çek Ekle'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Çek Tipi */}
            <div className="space-y-2">
              <Label>Çek Tipi</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={form.tip === 'alinan' ? 'default' : 'outline'}
                  className={form.tip === 'alinan' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  onClick={() => setForm({ ...form, tip: 'alinan' })}
                >
                  Alınan
                </Button>
                <Button
                  type="button"
                  variant={form.tip === 'verilen' ? 'default' : 'outline'}
                  className={form.tip === 'verilen' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                  onClick={() => setForm({ ...form, tip: 'verilen' })}
                >
                  Verilen
                </Button>
              </div>
            </div>

            {/* Çek No ve Banka */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cek_no">Çek No *</Label>
                <Input
                  id="cek_no"
                  placeholder="Çek numarası"
                  value={form.cek_no}
                  onChange={(e) => setForm({ ...form, cek_no: e.target.value })}
                  data-testid="cek-no-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="banka">Banka</Label>
                <Input
                  id="banka"
                  placeholder="Banka adı"
                  value={form.banka}
                  onChange={(e) => setForm({ ...form, banka: e.target.value })}
                />
              </div>
            </div>

            {/* Tutar */}
            <div className="space-y-2">
              <Label htmlFor="tutar">Tutar (TL) *</Label>
              <Input
                id="tutar"
                type="number"
                placeholder="0.00"
                value={form.tutar}
                onChange={(e) => setForm({ ...form, tutar: e.target.value })}
                data-testid="cek-tutar-input"
              />
            </div>

            {/* Cari Hesap */}
            <div className="space-y-2">
              <Label>Cari Hesap</Label>
              <Popover open={cariOpen} onOpenChange={setCariOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={cariOpen}
                    className="w-full justify-between font-normal"
                    data-testid="cek-cari-select"
                  >
                    {form.cari_hesap_adi || "Cari hesap seçin..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Cari hesap ara..." />
                    <CommandList>
                      <CommandEmpty>Cari hesap bulunamadı.</CommandEmpty>
                      <CommandGroup>
                        {cariHesaplar.map((cari) => (
                          <CommandItem
                            key={cari.id}
                            value={cari.ad}
                            onSelect={() => {
                              setForm({
                                ...form,
                                cari_hesap_id: cari.id,
                                cari_hesap_adi: cari.ad
                              });
                              setCariOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                form.cari_hesap_id === cari.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {cari.ad} {cari.il && `(${cari.il})`}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Hesap Kartı */}
            <div className="space-y-2">
              <Label>Hesap Kartı</Label>
              <Popover open={hesapKartiOpen} onOpenChange={setHesapKartiOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={hesapKartiOpen}
                    className="w-full justify-between font-normal"
                    data-testid="cek-hesap-karti-select"
                  >
                    {form.hesap_karti_adi || "Hesap kartı seçin..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Hesap kartı ara..." />
                    <CommandList>
                      <CommandEmpty>Hesap kartı bulunamadı.</CommandEmpty>
                      <CommandGroup>
                        {hesapKartlari.map((kart) => (
                          <CommandItem
                            key={kart.id}
                            value={kart.kayit_adi}
                            onSelect={() => {
                              setForm({
                                ...form,
                                hesap_karti_id: kart.id,
                                hesap_karti_adi: kart.kayit_adi
                              });
                              setHesapKartiOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                form.hesap_karti_id === kart.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {kart.kayit_adi}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Tarihler */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duzenleme_tarihi">Düzenleme Tarihi</Label>
                <Input
                  id="duzenleme_tarihi"
                  type="date"
                  value={form.duzenleme_tarihi}
                  onChange={(e) => setForm({ ...form, duzenleme_tarihi: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vade_tarihi">Vade Tarihi</Label>
                <Input
                  id="vade_tarihi"
                  type="date"
                  value={form.vade_tarihi}
                  onChange={(e) => setForm({ ...form, vade_tarihi: e.target.value })}
                />
              </div>
            </div>

            {/* Keşideci / Alıcı */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="kesideci">Keşideci</Label>
                <Input
                  id="kesideci"
                  placeholder="Çeki düzenleyen"
                  value={form.kesideci}
                  onChange={(e) => setForm({ ...form, kesideci: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="alici">Alıcı</Label>
                <Input
                  id="alici"
                  placeholder="Çek alıcısı"
                  value={form.alici}
                  onChange={(e) => setForm({ ...form, alici: e.target.value })}
                />
              </div>
            </div>

            {/* Açıklama */}
            <div className="space-y-2">
              <Label htmlFor="aciklama">Açıklama</Label>
              <Textarea
                id="aciklama"
                placeholder="Açıklama yazın..."
                value={form.aciklama}
                onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
                rows={2}
              />
            </div>

            {/* Belgeler - Çoklu Yükleme */}
            <div className="space-y-2">
              <Label>Belgeler (Çek Fotoğrafı)</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="cek-belge-upload"
                    multiple
                    data-testid="cek-belge-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('cek-belge-upload').click()}
                    className="gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Belge Ekle
                  </Button>
                  <span className="text-xs text-gray-500">Birden fazla dosya seçebilirsiniz</span>
                </div>
                {form.belgeler && form.belgeler.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.belgeler.map((belge, index) => (
                      <div key={index} className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm max-w-[150px] truncate">{belge.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => removeBelge(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {form.belgeler && form.belgeler.some(b => b.url?.startsWith('data:image')) && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {form.belgeler.filter(b => b.url?.startsWith('data:image')).map((belge, idx) => (
                      <img 
                        key={idx}
                        src={belge.url} 
                        alt={belge.name} 
                        className="max-h-20 rounded-lg border"
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              İptal
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="cek-submit-btn"
            >
              {editingCek ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        open={showDocPreview}
        onOpenChange={setShowDocPreview}
        belgeler={previewBelgeler}
        title="Çek Belgeleri"
      />

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Çek Detayı
            </DialogTitle>
          </DialogHeader>
          {detailRecord && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                <Badge className={detailRecord.tip === 'alinan' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {detailRecord.tip === 'alinan' ? 'Alınan Çek' : 'Verilen Çek'}
                </Badge>
                {detailRecord.durum === 'odenmis' ? (
                  <Badge className="bg-green-100 text-green-700">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Ödenmiş
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700">
                    <Clock className="h-3 w-3 mr-1" />
                    Ödenecek
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Çek No</Label>
                  <p className="font-mono font-semibold text-lg">{detailRecord.cek_no || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Banka</Label>
                  <p className="font-medium">{detailRecord.banka || '-'}</p>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-gray-500 text-sm">Cari Hesap</Label>
                <p className="font-medium">{detailRecord.cari_hesap_adi || '-'}</p>
              </div>

              <div className="space-y-1">
                <Label className="text-gray-500 text-sm">Tutar</Label>
                <p className="font-semibold text-blue-600 text-lg">{formatCurrency(detailRecord.tutar)}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Düzenleme Tarihi</Label>
                  <p className="font-medium">{formatDate(detailRecord.duzenleme_tarihi)}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Vade Tarihi</Label>
                  <p className="font-medium">{formatDate(detailRecord.vade_tarihi)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Keşideci</Label>
                  <p className="font-medium">{detailRecord.kesideci || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Alıcı</Label>
                  <p className="font-medium">{detailRecord.alici || '-'}</p>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-gray-500 text-sm">Etiket (Hesap Kartı)</Label>
                <p className="font-medium">{detailRecord.hesap_karti_adi || '-'}</p>
              </div>

              <div className="space-y-1">
                <Label className="text-gray-500 text-sm">Açıklama</Label>
                <p className="font-medium bg-gray-50 p-3 rounded-lg">{detailRecord.aciklama || 'Açıklama yok'}</p>
              </div>

              {detailRecord.belgeler && detailRecord.belgeler.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-gray-500 text-sm">Belgeler ({detailRecord.belgeler.length})</Label>
                  <div className="flex flex-wrap gap-2">
                    {detailRecord.belgeler.map((belge, idx) => (
                      <Badge key={idx} variant="outline" className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {belge.name || `Belge ${idx + 1}`}
                      </Badge>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setPreviewBelgeler(detailRecord.belgeler);
                      setShowDocPreview(true);
                    }}
                  >
                    Belgeleri Görüntüle
                  </Button>
                </div>
              )}

              <div className="pt-4 border-t text-xs text-gray-500">
                <p>Oluşturulma: {detailRecord.created_at ? new Date(detailRecord.created_at).toLocaleString('tr-TR') : '-'}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowDetailModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Detail Popup */}
      <JobDetailPopup
        jobId={selectedJobId}
        isOpen={showJobPopup}
        onClose={() => {
          setShowJobPopup(false);
          setSelectedJobId(null);
        }}
      />
    </div>
  );
};

export default AccountingChecks;
